import { mediaQueryLarge, isMobileBreakpoint } from '@theme/utilities';

// Accordion
class AccordionCustom extends HTMLElement {
  /** @type {HTMLDetailsElement} */
  get details() {
    const details = this.querySelector('details');

    if (!(details instanceof HTMLDetailsElement)) throw new Error('Details element not found');

    return details;
  }

  /** @type {HTMLElement} */
  get summary() {
    const summary = this.details.querySelector('summary');

    if (!(summary instanceof HTMLElement)) throw new Error('Summary element not found');

    return summary;
  }

  get #disableOnMobile() {
    return this.dataset.disableOnMobile === 'true';
  }

  get #disableOnDesktop() {
    return this.dataset.disableOnDesktop === 'true';
  }

  get #closeWithEscape() {
    return this.dataset.closeWithEscape === 'true';
  }

  #controller = new AbortController();

  connectedCallback() {
    const { signal } = this.#controller;

    this.#setDefaultOpenState();

    this.addEventListener('keydown', this.#handleKeyDown, { signal });
    this.summary.addEventListener('click', this.handleClick, { signal });
    mediaQueryLarge.addEventListener('change', this.#handleMediaQueryChange, { signal });
  }

  /**
   * Handles the disconnect event.
   */
  disconnectedCallback() {
    // Disconnect all the event listeners
    this.#controller.abort();
  }

  /**
   * Handles the click event.
   * @param {Event} event - The event.
   */
handleClick = (event) => {
  const isMobile = isMobileBreakpoint();
  const isDesktop = !isMobile;

  // Stop default behaviour from the browser
  if ((isMobile && this.#disableOnMobile) || (isDesktop && this.#disableOnDesktop)) {
    event.preventDefault();
    return;
  }

  // Only run accordion group behavior on mobile
  if (isMobile) {
    const parent = this.parentElement;

    if (!parent) return;

    const accordions = parent.querySelectorAll('accordion-custom');

    accordions.forEach((accordion) => {
  if (accordion !== this && !accordion.classList.contains('social-menu-accordion')) {
    const details = accordion.querySelector('details');
    if (details) {
      details.open = false;
    }
  }
});
  }
};

  /**
   * Handles the media query change event.
   */
  #handleMediaQueryChange = () => {
    this.#setDefaultOpenState();
  };

  /**
   * Sets the default open state of the accordion based on the `open-by-default-on-mobile` and `open-by-default-on-desktop` attributes.
   */
  #setDefaultOpenState() {
    const isMobile = isMobileBreakpoint();

    this.details.open =
      (isMobile && this.hasAttribute('open-by-default-on-mobile')) ||
      (!isMobile && this.hasAttribute('open-by-default-on-desktop'));
  }

  /**
   * Handles keydown events for the accordion
   *
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #handleKeyDown(event) {
    // Close the accordion when used as a menu
    if (event.key === 'Escape' && this.#closeWithEscape) {
      event.preventDefault();

      this.details.open = false;
      this.summary.focus();
    }
  }
}

if (!customElements.get('accordion-custom')) {
  customElements.define('accordion-custom', AccordionCustom);
}



function initDrawerAccordionMobile() {
  if (!isMobileBreakpoint()) return;

  const menus = document.querySelectorAll('.menu-drawer__menu--childlist');

  menus.forEach((menu) => {
    const accordions = menu.querySelectorAll('accordion-custom details');

    let firstOpen = null;

    accordions.forEach((details) => {
      if (details.open && !firstOpen) {
        firstOpen = details;
      } else if (details.open) {
        details.open = false;
      }
    });

    accordions.forEach((details) => {
      details.addEventListener('toggle', () => {
        if (!details.open) return;

        accordions.forEach((other) => {
          if (other !== details) {
            other.open = false;
          }
        });
      });
    });
  });
}

document.addEventListener('DOMContentLoaded', initDrawerAccordionMobile);

// Fallback for Safari's lack of interpolate-size support
document.addEventListener('click', function(e) {
  if (CSS.supports && CSS.supports('interpolate-size', 'allow-keywords')) return;

  const summary = e.target.closest('accordion-custom summary');
  if (!summary) return;

  const details = summary.parentElement;
  const content = details.querySelector('.details-content');
  if (!content) return;

  e.preventDefault();
  
  // Custom prevent-double-click logic
  if (content.dataset.isAnimating === 'true') return;
  content.dataset.isAnimating = 'true';

  if (!details.open) {
    details.open = true;
    content.style.overflow = 'hidden';
    const height = content.scrollHeight + 'px';
    
    const animation = content.animate(
      [
        { height: '0px', opacity: 0 },
        { height: height, opacity: 1 }
      ],
      { duration: 300, easing: 'ease-out' }
    );
    
    animation.onfinish = () => {
      content.style.overflow = '';
      content.dataset.isAnimating = 'false';
    };
  } else {
    content.style.overflow = 'hidden';
    const height = content.offsetHeight + 'px';
    
    const animation = content.animate(
      [
        { height: height, opacity: 1 },
        { height: '0px', opacity: 0 }
      ],
      { duration: 300, easing: 'ease-out' }
    );
    
    animation.onfinish = () => {
      details.open = false;
      content.style.overflow = '';
      content.dataset.isAnimating = 'false';
      details.dispatchEvent(new Event('toggle'));
    };
  }
});

document.addEventListener("DOMContentLoaded", () => {
  const accordions = document.querySelectorAll(".facets__filters-wrapper accordion-custom");

  accordions.forEach((accordion) => {
    const details = accordion.querySelector("details");

    if (!details) return;

    details.addEventListener("toggle", () => {
      if (!details.open) return;

      accordions.forEach((otherAccordion) => {
        if (otherAccordion !== accordion) {
          const otherDetails = otherAccordion.querySelector("details");
          if (otherDetails) {
            otherDetails.open = false;
          }
        }
      });
    });
  });
});



document.addEventListener('click', function (e) {
  const clickedDetails = e.target.closest('.product-details accordion-custom details');
  if (!clickedDetails) return;
  const parentAccordion = clickedDetails.closest('accordion-custom');
  if (!parentAccordion) return;
  const siblings = Array.from(parentAccordion.parentElement.children)
    .filter(el => el !== parentAccordion && el.tagName.toLowerCase() === 'accordion-custom');
  siblings.forEach(acc => {
    acc.querySelectorAll('details[open]').forEach(d => {
      d.removeAttribute('open');
    });
  });
});

/**
 * facets-accordion-single.js
 *
 * Ensures only one accordion-custom is open at a time across:
 *   - sorting-filter-component  (the sort dropdown)
 *   - .facets__filters-wrapper  (all filter accordions)
 *
 * When any accordion in this group opens, every other one closes.
 */

(function () {
  function initFacetsSingleAccordion() {
    // Collect ALL accordions that should be part of the same mutual-exclusive group.
    // 1. The sort accordion inside sorting-filter-component
    // 2. Every filter accordion inside .facets__filters-wrapper
    const sortingAccordions = document.querySelectorAll(
      'sorting-filter-component accordion-custom'
    );
    const filterAccordions = document.querySelectorAll(
      '.facets__filters-wrapper accordion-custom'
    );

    // Merge into one flat array — this is the "group"
    const allAccordions = [...sortingAccordions, ...filterAccordions];

    if (allAccordions.length === 0) return;

    /**
     * Close every accordion in the group except the one that just opened.
     * @param {Element} openedAccordion
     */
    function closeOthers(openedAccordion) {
      allAccordions.forEach((accordion) => {
        if (accordion === openedAccordion) return;

        const details = accordion.querySelector('details');
        if (details && details.open) {
          details.open = false;
        }
      });
    }

    // Attach a toggle listener to each accordion's <details>
    allAccordions.forEach((accordion) => {
      const details = accordion.querySelector('details');
      if (!details) return;

      details.addEventListener('toggle', () => {
        // Only react when this accordion just opened
        if (!details.open) return;
        closeOthers(accordion);
      });
    });
  }

  // Run after the DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initFacetsSingleAccordion);
  } else {
    initFacetsSingleAccordion();
  }
})();


document.addEventListener("DOMContentLoaded", () => {
  const accordions = document.querySelectorAll(".facets__filters-wrapper accordion-custom");

  accordions.forEach((accordion) => {
    const details = accordion.querySelector("details");

    if (!details) return;

    details.addEventListener("toggle", () => {
      if (!details.open) return;

      accordions.forEach((otherAccordion) => {
        if (otherAccordion !== accordion) {
          const otherDetails = otherAccordion.querySelector("details");
          if (otherDetails) {
            otherDetails.open = false;
          }
        }
      });
    });
  });
});

document.addEventListener('DOMContentLoaded', () => {
  // Get ALL accordion-custom ONLY inside .accordion
  const accordions = document.querySelectorAll('.accordion accordion-custom');

  accordions.forEach((accordion) => {
    const details = accordion.querySelector('details');
    if (!details) return;

    details.addEventListener('toggle', () => {
      if (!details.open) return;

      // Close ALL others inside ANY .accordion
      accordions.forEach((otherAccordion) => {
        if (otherAccordion !== accordion) {
          const otherDetails = otherAccordion.querySelector('details');
          if (otherDetails && otherDetails.open) {
            otherDetails.open = false;
          }
        }
      });
    });
  });
});